import { useAuthContext } from "BEDROCK/auth/auth-provider";
import { useQuery } from "@tanstack/react-query";

interface User {
  id: string;
  email: string;
  created_at: string;
  last_sign_in_at: string;
  user_metadata: Record<string, any>;
}

interface UseUsersOptions {
  limit?: number;
  offset?: number;
  searchTerm?: string;
}

export const useUsers = (options: UseUsersOptions = {}) => {
  const { client, session } = useAuthContext();
  const { limit = 20, offset = 0, searchTerm } = options;

  const getUsers = async (): Promise<{ users: User[]; total: number }> => {
    if (!client) throw new Error("useUsers: Client not initialized");
    if (!session) throw new Error("useUsers: No active session");

    const { data, error } = await client.functions.invoke("admin-list-users", {
      headers: {
        Authorization: `Bearer ${session.access_token}`,
      },
      body: {
        limit,
        offset,
        searchTerm: searchTerm || null,
      },
    });

    if (error) throw error;
    return { users: data.users || [], total: data.total || 0 };
  };

  const {
    data,
    refetch,
    isLoading,
    isFetching,
    isError,
  } = useQuery({
    queryKey: ["admin-users", limit, offset, searchTerm],
    queryFn: getUsers,
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
    enabled: !!session,
  });

  return {
    users: data?.users,
    total: data?.total || 0,
    refetch,
    isLoading,
    isFetching,
    isError,
  };
};
